import React, { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { 
  HiCheck,
  HiXMark,
  HiUserPlus,
  HiArchiveBox,
  HiFlag,
  HiClock,
  HiCheckCircle,
  HiChevronDown,
  HiEllipsisHorizontal
} from 'react-icons/hi2';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { TicketStatus, TicketPriority, type User } from '@/types/api';
import type { BulkActionsBarProps } from '@/types/ui';

import { userService } from '@/services/userService';

/**
 * Bulk Actions Bar Component - Multi-select operations for tickets
 * Provides comprehensive bulk operations with intuitive interface
 */
export const BulkActionsBar: React.FC<Omit<BulkActionsBarProps, 'onBulkDelete'>> = ({
  selectedCount,
  totalCount,
  onSelectAll,
  onSelectNone,
  onBulkStatusChange,
  onBulkPriorityChange,
  onBulkAssign,
  actions = [],
  loading = false,
  position = 'top',
  className,
  'data-testid': testId,
  ...props
}) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<User[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const statusMenuRef = useRef<HTMLDivElement>(null);
  const priorityMenuRef = useRef<HTMLDivElement>(null);
  const assignMenuRef = useRef<HTMLDivElement>(null);
  const moreActionsRef = useRef<HTMLDivElement>(null);

  // Load agents on opening assign menu
  useEffect(() => {
    if (showAssignMenu && availableAgents.length === 0 && !loadingAgents) {
      setLoadingAgents(true);
      userService.getAllAgents()
        .then(res => {
          if (res.success && res.data) {
            setAvailableAgents(res.data.data);
          }
        })
        .catch(err => console.error('Failed to load agents in BulkActionsBar:', err))
        .finally(() => setLoadingAgents(false));
    }
  }, [showAssignMenu, availableAgents.length, loadingAgents]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target as Node)) {
        setShowStatusMenu(false);
      }
      if (priorityMenuRef.current && !priorityMenuRef.current.contains(event.target as Node)) {
        setShowPriorityMenu(false);
      }
      if (assignMenuRef.current && !assignMenuRef.current.contains(event.target as Node)) {
        setShowAssignMenu(false);
      }
      if (moreActionsRef.current && !moreActionsRef.current.contains(event.target as Node)) {
        setShowMoreActions(false);
      }
    };

    if (showStatusMenu || showPriorityMenu || showAssignMenu || showMoreActions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [showStatusMenu, showPriorityMenu, showAssignMenu, showMoreActions]);

  // Status options
  const statusOptions = [
    { value: TicketStatus.Open, label: 'Open', icon: HiFlag, color: 'text-blue-600' },
    { value: TicketStatus.Pending, label: 'Pending', icon: HiClock, color: 'text-yellow-600' },
    { value: TicketStatus.OnHold, label: 'On Hold', icon: HiClock, color: 'text-gray-600' },
    { value: TicketStatus.Resolved, label: 'Resolved', icon: HiCheckCircle, color: 'text-green-600' },
    { value: TicketStatus.Closed, label: 'Closed', icon: HiArchiveBox, color: 'text-gray-600' }
  ];

  // Priority options
  const priorityOptions = [
    { value: TicketPriority.Low, label: 'Low', color: 'bg-green-100 text-green-800 border-green-200' },
    { value: TicketPriority.Medium, label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    { value: TicketPriority.High, label: 'High', color: 'bg-orange-100 text-orange-800 border-orange-200' },
    { value: TicketPriority.Urgent, label: 'Urgent', color: 'bg-red-100 text-red-800 border-red-200' }
  ];

  const allActions = actions;

  const handleStatusChange = (status: TicketStatus) => {
    onBulkStatusChange(status);
    setShowStatusMenu(false);
  };

  const handleStatusMenuToggle = () => {
    const newState = !showStatusMenu;
    setShowStatusMenu(newState);
    if (newState) {
      setShowPriorityMenu(false);
      setShowMoreActions(false);
    }
  };

  const handlePriorityMenuToggle = () => {
    const newState = !showPriorityMenu;
    setShowPriorityMenu(newState);
    if (newState) {
      setShowStatusMenu(false);
      setShowMoreActions(false);
    }
  };

  const handleMoreActionsToggle = () => {
    const newState = !showMoreActions;
    setShowMoreActions(newState);
    if (newState) {
      setShowStatusMenu(false);
      setShowPriorityMenu(false);
    }
  };

  const handlePriorityChange = (priority: TicketPriority) => {
    onBulkPriorityChange(priority);
    setShowPriorityMenu(false);
  };


  const barClasses = clsx(
    // Base styles
    'bg-white',
    'border',
    'border-gray-200',
    'shadow-sm',
    'transition-all',
    'duration-300',
    'z-10',
    
    // Position specific
    position === 'floating' && [
      'fixed',
      'bottom-6',
      'left-1/2',
      'transform',
      '-translate-x-1/2',
      'rounded-lg',
      'shadow-lg',
      'max-w-4xl',
      'mx-4'
    ],
    
    position === 'top' && [
      'rounded-b-lg',
      'border-t-0'
    ],
    
    position === 'bottom' && [
      'rounded-t-lg',
      'border-b-0'
    ],
    
    // Loading state
    loading && 'opacity-75',
    
    className
  );

  // Only show if there are selected items
  if (selectedCount === 0) {
    return null;
  }

  return (
    <div
      className={barClasses}
      data-testid={testId}
      role="toolbar"
      aria-label={`Bulk actions for ${selectedCount} selected tickets`}
      {...props}
    >
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left Section - Selection Info */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Badge variant="primary" size="sm">
              {selectedCount}
            </Badge>
            <span className="text-sm font-medium text-gray-700">
              {selectedCount === 1 ? 'ticket' : 'tickets'} selected
            </span>
          </div>

          {/* Select All/None */}
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={selectedCount === totalCount ? onSelectNone : onSelectAll}
              className="text-primary-600 hover:text-primary-700"
            >
              {selectedCount === totalCount ? (
                <>
                  <HiXMark className="w-4 h-4 mr-1" />
                  Clear All
                </>
              ) : (
                <>
                  <HiCheck className="w-4 h-4 mr-1" />
                  Select All ({totalCount})
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center space-x-2">
          {/* Status Dropdown */}
          <div className="relative" ref={statusMenuRef}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleStatusMenuToggle}
              disabled={loading}
              className="relative flex items-center pr-6 min-w-[75px]"
            >
              <HiFlag className="w-4 h-4 mr-1.5" />
              <span>Status</span>
              <HiChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 transform -translate-y-1/2" />
            </Button>
            
            {showStatusMenu && (
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-20 min-w-40">
                {statusOptions.map((status) => {
                  const Icon = status.icon;
                  return (
                    <button
                      key={status.value}
                      onClick={() => handleStatusChange(status.value)}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Icon className={clsx('w-4 h-4', status.color)} />
                      <span>{status.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Priority Dropdown */}
          <div className="relative" ref={priorityMenuRef}>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePriorityMenuToggle}
              disabled={loading}
              className="relative flex items-center pr-6 min-w-[85px]"
            >
              <HiFlag className="w-4 h-4 mr-1.5" />
              <span>Priority</span>
              <HiChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 transform -translate-y-1/2" />
            </Button>
            
            {showPriorityMenu && (
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-30 min-w-36">
                {priorityOptions.map((priority) => (
                  <button
                    key={priority.value}
                    onClick={() => handlePriorityChange(priority.value)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    <Badge size="sm" className={priority.color}>
                      {priority.label}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assign Button & Dropdown */}
          <div className="relative" ref={assignMenuRef}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAssignMenu(!showAssignMenu)}
              disabled={loading}
              className="flex items-center gap-1.5 min-w-[80px]"
            >
              <HiUserPlus className="w-4 h-4" />
              <span>Assign</span>
            </Button>

            {showAssignMenu && (
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-30 min-w-48 max-h-60 overflow-y-auto py-1">
                {loadingAgents ? (
                  <div className="px-3 py-2 text-xs text-gray-400 italic">Loading agents...</div>
                ) : availableAgents.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-400 italic">No agents available</div>
                ) : (
                  availableAgents.map((agent) => (
                    <button
                      key={agent.id}
                      onClick={() => {
                        onBulkAssign?.(agent.id);
                        setShowAssignMenu(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 truncate"
                    >
                      <span className="truncate">{agent.fullName || `${agent.firstName} ${agent.lastName}`}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {allActions.length > 0 && <div className="w-px h-6 bg-gray-300" />}

          {/* Additional Actions */}
          {allActions.slice(0, 2).map((action) => (
            <Button
              key={action.key}
              variant={action.variant || 'outline'}
              size="sm"
              onClick={action.onClick}
              disabled={loading || action.disabled}
              className="flex items-center gap-1.5"
              title={action.label}
            >
              {action.icon}
              <span className="hidden sm:inline">{action.label}</span>
            </Button>
          ))}

          {/* More Actions Dropdown */}
          {allActions.length > 2 && (
            <div className="relative" ref={moreActionsRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMoreActionsToggle}
                disabled={loading}
                className=""
                aria-label="More bulk actions"
                aria-haspopup="true"
                aria-expanded={showMoreActions}
              >
                <HiEllipsisHorizontal className="w-4 h-4" />
              </Button>
              
              {showMoreActions && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-40 min-w-40">
                  {allActions.slice(2).map((action) => (
                    <button
                      key={action.key}
                      onClick={() => {
                        action.onClick();
                        setShowMoreActions(false);
                      }}
                      disabled={action.disabled}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {action.icon}
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Close/Clear Selection */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onSelectNone}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600"
            title="Clear selection"
          >
            <HiXMark className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
            <span className="text-sm text-gray-600">Processing...</span>
          </div>
        </div>
      )}
    </div>
  );
};
